import { create } from 'zustand';
import { reaction } from 'mobx';
import axios from 'axios';
import { store } from 'Models/Store';
import { DYNAMIC_BOOKING_FIELD_API_URL } from 'Constants';
import type {
	DynamicBookingFieldEntityDto,
	DynamicFieldBookingType,
} from 'Modules/DynamicBookingFields/Types/DynamicBookingFieldFormSchema';
import { dtoToForm } from 'Modules/DynamicBookingFields/Types/DynamicBookingFieldFormSchema';

export interface CachedDynamicField extends DynamicBookingFieldEntityDto {
	/** Parsed booking-type list, cached so wizard filters don't re-split CSV on every render. */
	bookingTypesParsed: DynamicFieldBookingType[];
	/** Customer-type ids the field is restricted to. Empty = visible to every subject. */
	userDisplayNameIds: string[];
}

interface DynamicBookingFieldsState {
	fields: CachedDynamicField[];
	loaded: boolean;
	loading: boolean;
	error: string | null;
	loadFields: (force?: boolean) => Promise<void>;
	invalidate: () => void;
}

const PARSE_TYPES = (csv: string | null | undefined): DynamicFieldBookingType[] => {
	if (!csv) return [];
	return csv
		.split(',')
		.map(s => s.trim())
		.filter(Boolean) as DynamicFieldBookingType[];
};

/**
 * Caches dynamic-field definitions for the wizard, booking-detail view, and
 * check-in. Loaded once on app startup; admin saves call `invalidate()` so the
 * next read refetches.
 */
export const useDynamicBookingFieldsStore = create<DynamicBookingFieldsState>((set, get) => ({
	fields: [],
	loaded: false,
	loading: false,
	error: null,

	loadFields: async (force = false) => {
		const state = get();
		if (!force && (state.loaded || state.loading)) return;

		set({ loading: true, error: null });
		try {
			const response = await axios.get(`${DYNAMIC_BOOKING_FIELD_API_URL}/active`);
			const list = (response.data ?? []) as DynamicBookingFieldEntityDto[];
			const cached: CachedDynamicField[] = list.map(dto => ({
				...dto,
				bookingTypesParsed: PARSE_TYPES(dto.bookingTypes),
				userDisplayNameIds: (dto.userDisplayNames ?? []).map(j => j.userDisplayNameId),
			}));
			set({ fields: cached, loaded: true, loading: false });
		} catch (e: any) {
			console.error('Failed to load dynamic booking fields', e);
			set({ loading: false, error: e?.message ?? 'Failed to load dynamic fields' });
		}
	},

	invalidate: () => set({ loaded: false }),
}));

// Field definitions are auth-gated: `/active` returns [] for anonymous visitors.
// When login state flips (e.g. signing in mid-way through the booking wizard) the
// cached list — empty for an anonymous visitor — would otherwise stick until a
// hard refresh. Invalidating pushes `loaded: false` to subscribed consumers so the
// next read refetches with the new session. Fires on both login and logout.
reaction(
	() => store.loggedIn,
	() => useDynamicBookingFieldsStore.getState().invalidate(),
);

/**
 * Filters cached fields for the booking-wizard tab: matching booking type,
 * visible to the booking-subject's UserDisplayName, not archived. The `archived`
 * exclusion only applies here — historical detail views/exports use the full
 * list so archived field labels still resolve.
 */
export function getVisibleDynamicFields(
	fields: CachedDynamicField[],
	bookingType: DynamicFieldBookingType,
	subjectUserDisplayNameId: string | null | undefined,
): CachedDynamicField[] {
	return fields
		.filter(f => !f.archived)
		.filter(f => f.bookingTypesParsed.includes(bookingType))
		.filter(f => {
			if (f.userDisplayNameIds.length === 0) return true;
			if (!subjectUserDisplayNameId) return false;
			return f.userDisplayNameIds.includes(subjectUserDisplayNameId);
		})
		.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

/**
 * Filters cached fields for a charter: aimed at charters, not archived, asked of the charter's
 * customer, in the admin's order.
 *
 * The customer-type rule is the booking-wizard list's, with the charter's customer standing in for
 * the booking subject — a field restricted to customer types is asked only of a customer of one of
 * them. A charter with no customer is asked the unrestricted fields only: a reserve is routinely a
 * hold on the ferry with nobody named on it yet, and there is nobody a restricted field could be
 * right for until somebody is. A customer with no customer type is the same case.
 *
 * Read the customer's type off the charter or off the picked option, both of which carry it, rather
 * than fetching it — a list that settles a moment after the panel opens shows the tab and then takes
 * it away again.
 */
export function getVisibleCharterFields(
	fields: CachedDynamicField[],
	customerUserDisplayNameId: string | null | undefined,
): CachedDynamicField[] {
	return fields
		.filter(f => !f.archived)
		.filter(f => f.appliesToCharters)
		.filter(f => {
			if (f.userDisplayNameIds.length === 0) return true;
			if (!customerUserDisplayNameId) return false;
			return f.userDisplayNameIds.includes(customerUserDisplayNameId);
		})
		.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

/**
 * Lookup a field by id from the cache. Used for rendering historical/archived
 * values on detail views and exports.
 */
export function getFieldById(fields: CachedDynamicField[], fieldId: string): CachedDynamicField | undefined {
	return fields.find(f => f.id === fieldId);
}

/**
 * Normalises the alteration's `dynamicFieldValues` into a flat {fieldId, value}
 * list. Newtonsoft serialises the dictionary either as an array of
 * `{ fieldId, value }` entries or as a plain object keyed by field id, so both
 * shapes are handled.
 */
export function parseDynamicFieldValues(raw: any): Array<{ fieldId: string; value: string }> {
	if (!raw) return [];
	if (Array.isArray(raw)) {
		return raw
			.filter((e: any) => e && e.fieldId)
			.map((e: any) => ({ fieldId: String(e.fieldId), value: String(e.value ?? '') }));
	}
	if (typeof raw === 'object') {
		return Object.entries(raw).map(([fieldId, value]) => ({ fieldId, value: String(value ?? '') }));
	}
	return [];
}

/**
 * Resolves the dynamic-field values stored on a booking's most-recent alteration
 * into the (non-archived) field definitions that have a non-empty value. Used by
 * the check-in list to decide which DOT / ICON / TAG indicators to render. Reads
 * `booking.bookedSummary.alterationContext.dynamicFieldValues`, which both
 * BookingEntity and CheckInBookingOverviewDto carry.
 */
export function getCheckInDisplayEntries(
	fields: CachedDynamicField[],
	booking: any,
): Array<{ field: CachedDynamicField; value: string }> {
	const raw = booking?.bookedSummary?.alterationContext?.dynamicFieldValues;
	return parseDynamicFieldValues(raw)
		.filter(e => e.value && e.value.trim() !== '')
		.map(e => ({ field: getFieldById(fields, e.fieldId), value: e.value }))
		.filter((e): e is { field: CachedDynamicField; value: string } => !!e.field && !e.field.archived);
}

// Re-export the form helper so consumers only need this one entry point.
export { dtoToForm };
